import { useMutation } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState, type ChangeEvent, type FormEvent } from 'react'

import { Button, ErrorText, Field, Input, Modal } from '@/components/ui'
import { useMuscleLabels, type Exercise } from '@/pages/workouts/api'
import { EQUIPMENT_LABELS } from '@/pages/workouts/logic'

import { ExerciseForm } from './ExerciseForm'
import { ExerciseInfoSheet } from './ExerciseInfo'
import { prepareRecognitionPhoto, recognizeExercise } from './recognition-api'

export function ExerciseRecognition({
  onClose, onPick, initialText = '', excludeId,
}: {
  onClose: () => void
  onPick: (exercise: Exercise) => void
  initialText?: string
  excludeId?: string
}) {
  const [text, setText] = useState(initialText)
  const [photo, setPhoto] = useState<File | null>(null)
  const [preview, setPreview] = useState('')
  const [photoError, setPhotoError] = useState('')
  const [preparing, setPreparing] = useState(false)
  const [editing, setEditing] = useState(false)
  const [info, setInfo] = useState<Exercise | null>(null)
  const cameraRef = useRef<HTMLInputElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const resultRef = useRef<HTMLElement>(null)
  const controller = useRef<AbortController | null>(null)
  const photoVersion = useRef(0)
  const photoId = useId()
  const muscle = useMuscleLabels()
  const recognition = useMutation({
    mutationFn: () => {
      controller.current?.abort()
      controller.current = new AbortController()
      return recognizeExercise(text, photo, controller.current.signal)
    },
    retry: false,
  })
  useEffect(() => () => {
    controller.current?.abort()
    photoVersion.current += 1
  }, [])
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

  const changePhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    const version = ++photoVersion.current
    recognition.reset()
    setPhotoError('')
    setPhoto(null)
    setPreview('')
    setPreparing(true)
    try {
      const prepared = await prepareRecognitionPhoto(file)
      if (version === photoVersion.current) {
        setPhoto(prepared)
        setPreview(URL.createObjectURL(prepared))
      }
    } catch (error) {
      if (version === photoVersion.current) setPhotoError(error instanceof Error ? error.message : 'Не удалось открыть фото')
    } finally {
      if (version === photoVersion.current) setPreparing(false)
    }
  }
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if ((text.trim() || photo) && !preparing && !recognition.isPending) recognition.mutate()
  }
  const pick = (exercise: Exercise) => {
    onPick(exercise)
    onClose()
  }
  const result = recognition.data
  useEffect(() => {
    // The submitted form disappears on success. Keep keyboard focus inside the dialog.
    ;(result ? resultRef.current : formRef.current)?.focus()
  }, [result])
  const matches = result?.matches.filter((match) => match.exercise.id !== excludeId) ?? []
  const busy = preparing || recognition.isPending

  return (
    <>
      <Modal open onClose={onClose} title="Распознать тренажёр" className="sm:max-w-lg" focusPanel>
        {result ? (
          <Button size="sm" onClick={() => recognition.reset()}>Изменить фото или описание</Button>
        ) : <form ref={formRef} tabIndex={-1} onSubmit={submit} className="grid gap-4 outline-none">
          <p className="text-sm text-muted">Сними тренажёр и его табличку или напиши название. ИИ найдёт упражнение в справочнике или подготовит новое.</p>
          <Field id={photoId} label="Фото тренажёра или таблички">
            <div className="flex flex-wrap gap-2">
              <Button disabled={busy} onClick={() => cameraRef.current?.click()}>Снять фото</Button>
              <Button disabled={busy} onClick={() => fileRef.current?.click()}>Выбрать фото</Button>
            </div>
            <input ref={cameraRef} id={photoId} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="sr-only" tabIndex={-1} disabled={busy} onChange={(event) => void changePhoto(event)} />
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" aria-label="Выбрать файл с фото" className="sr-only" tabIndex={-1} disabled={busy} onChange={(event) => void changePhoto(event)} />
          </Field>
          {photo && preview ? (
            <div className="flex items-start gap-3">
              <img src={preview} alt="Фото для распознавания" className="h-32 w-32 rounded-lg object-contain bg-surface-2" />
              <Button size="sm" disabled={busy} onClick={() => { setPhoto(null); setPreview(''); recognition.reset() }}>Убрать фото</Button>
            </div>
          ) : null}
          {photoError ? <ErrorText>{photoError}</ErrorText> : null}
          <Input
            label="Название или описание" placeholder="Например, дельтоид стоя Foreman FS-416"
            hint="Можно описать, как двигаются руки или ноги. Если есть фото, текст необязателен."
            maxLength={2000} value={text} disabled={busy}
            onChange={(event) => { setText(event.target.value); recognition.reset() }}
          />
          <p className="text-xs text-muted">Для распознавания нужна сеть. Фото отправится настроенному ИИ и не сохранится в справочнике. Для фото модель должна поддерживать изображения.</p>
          <Button type="submit" variant="primary" disabled={preparing || (!text.trim() && !photo)} loading={recognition.isPending}>
            {preparing ? 'Подготовка фото…' : recognition.isPending ? 'Распознаём…' : 'Распознать'}
          </Button>
          {recognition.error ? <ErrorText error={recognition.error} /> : null}
        </form>}
        {result ? (
          <section ref={resultRef} tabIndex={-1} className="mt-5 border-t border-border pt-4 outline-none" aria-label="Результат распознавания" aria-live="polite">
            <h3 className="font-medium text-fg">{result.recognized ? result.draft?.name : 'Нужно уточнить'}</h3>
            <p className="mt-2 whitespace-pre-line text-sm text-muted">{result.summary}</p>
            {matches.length > 0 ? (
              <ul className="mt-4 divide-y divide-border">
                {matches.map(({ exercise, relation, reason }) => (
                  <li key={exercise.id} className="py-3">
                    <p className="text-xs font-medium text-accent">{relation === 'exact' ? 'Уже есть в справочнике' : 'Похожее упражнение'}</p>
                    <p className="mt-1 break-words font-medium text-fg">{exercise.name}</p>
                    <p className="mt-1 text-xs text-muted">{muscle(exercise.primary_muscle)} · {EQUIPMENT_LABELS[exercise.equipment]}</p>
                    <p className="mt-2 text-sm text-muted">{reason}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => pick(exercise)}>Выбрать</Button>
                      <Button size="sm" variant="ghost" onClick={() => setInfo(exercise)}>Техника и мышцы</Button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : result.recognized ? <p className="mt-3 text-sm text-muted">Подходящих упражнений в справочнике не найдено.</p> : null}
            {result.draft ? (
              <div className="mt-4 grid gap-2">
                <Button icon="plus" onClick={() => setEditing(true)}>Проверить и создать своё</Button>
                <p className="text-xs text-muted">Отдельная карточка сохранит свою историю весов и рекордов.</p>
              </div>
            ) : null}
          </section>
        ) : null}
      </Modal>
      {editing && result?.draft ? (
        <ExerciseForm open exercise={null} initialValues={result.draft} onClose={() => setEditing(false)} onSaved={pick} saveLabel="Сохранить и выбрать" />
      ) : null}
      {info ? <ExerciseInfoSheet open exerciseId={info.id} name={info.name} exercise={info} onClose={() => setInfo(null)} /> : null}
    </>
  )
}
